
import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";
import { useIsMobile } from "@/hooks/use-mobile";
import { useWatchlist } from "@/context/WatchlistContext";
import { 
  LineChart, 
  Eye, 
  Newspaper, 
  Bell, 
  Settings
} from "lucide-react";

type NavItem = {
  label: string;
  path: string;
  icon: React.ReactNode;
};

const navItems: NavItem[] = [
  {
    label: "Market Watch",
    path: "/",
    icon: <LineChart className="h-5 w-5" />
  },
  {
    label: "Watch List",
    path: "/watchlist",
    icon: <Eye className="h-5 w-5" />
  },
  {
    label: "News",
    path: "/news",
    icon: <Newspaper className="h-5 w-5" />
  },
  {
    label: "Alerts",
    path: "/alerts",
    icon: <Bell className="h-5 w-5" />
  },
  {
    label: "Settings",
    path: "/settings",
    icon: <Settings className="h-5 w-5" />
  }
];

export const Navigation = () => {
  const isMobile = useIsMobile();
  const location = useLocation();
  const navigate = useNavigate();
  const { setActiveView } = useWatchlist();
  
  const isActive = (path: string) => {
    // For home route, do exact match
    if (path === "/") {
      return location.pathname === path;
    }
    // For other routes, check if path is included
    return location.pathname.includes(path);
  };

  const handleNavigation = (path: string) => {
    // If navigating to Market Watch (home), reset the sub-tab to "market"
    if (path === "/" && location.pathname !== "/") {
      setActiveView("market");
    }
    navigate(path);
  };

  if (isMobile) {
    // Mobile bottom navigation
    return (
      <nav className="fixed bottom-0 left-0 z-40 w-full bg-background border-t">
        <div className="grid grid-cols-5 h-14">
          {navItems.map((item) => (
            <button
              key={item.path}
              onClick={() => handleNavigation(item.path)}
              className={cn(
                "flex flex-col items-center justify-center px-2 transition-colors",
                isActive(item.path) 
                  ? "text-primary" 
                  : "text-muted-foreground hover:text-primary"
              )}
            >
              <div className="flex items-center justify-center">
                {item.icon}
              </div>
              <span className="text-[0.65rem] mt-1 font-medium">{item.label}</span>
            </button>
          ))}
        </div>
      </nav>
    );
  }

  // Desktop top navigation
  return (
    <div className="hidden md:block">
      <nav className="flex space-x-4">
        {navItems.map((item) => (
          <button
            key={item.path}
            onClick={() => handleNavigation(item.path)}
            className={cn(
              "flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors",
              isActive(item.path)
                ? "bg-accent text-accent-foreground"
                : "text-muted-foreground hover:text-primary hover:bg-accent/50"
            )}
          >
            <div className="mr-2">
              {item.icon}
            </div>
            {item.label}
          </button>
        ))}
      </nav>
    </div>
  );
};
